/* global Modal */

/**
 * Free-tattoo picker overlay.
 *
 * Lazy by design: nothing is fetched until the first open. Results come from
 * the layout-less `free-products` templates, applied with ?view= so the
 * response is just the results section — for the picker collection
 * (/collections/<handle>?view=free-products) and for search
 * (/search?view=free-products&q=…). 20 per page, IntersectionObserver
 * infinite scroll.
 *
 * The cards list the ORIGINAL products; each carries the variant id of its
 * free copy (custom.free_copy_product) in data-variant-id — that is what
 * goes into the cart.
 *
 * Modes:
 * - choose:   pick up to `allowed` free tattoos (nothing in cart yet)
 * - claim:    same as choose, but opened by the reward engine when checkout
 *             was clicked with free tattoos earned and none picked — adds
 *             the congrats headline
 * - swap:     replace the free tattoos currently in the cart
 * - reselect: opened by the reward engine after a downgrade removed all free
 *             lines; may offer the restore-original button
 *
 * On confirm the picker owns the cart mutations: adds the newly selected
 * free variants (line property _free_tattoo), removes de-selected free
 * lines, persists the selection in the _reward_picks cart attribute, then
 * dispatches on:cart:change so the drawer/header/sticky bar re-render. The
 * reward engine (tattoo-rewards.js) re-validates after every cart change, so
 * a failed step here can never yield unearned free items.
 */

/**
 * Canonical "is this a reward line" test. Delegates to the engine so the two
 * files can never drift; the inline fallback matches it exactly for the case
 * where the engine failed to load.
 * @param {object} item - Cart line item from /cart.js.
 * @returns {boolean}
 */
function isFreeLine(item) {
  if (window.TattooRewards && window.TattooRewards.isFreeLine) {
    return window.TattooRewards.isFreeLine(item);
  }
  return Boolean(item.properties && item.properties._free_tattoo) || item.original_price === 0;
}

class FreeTattooPicker extends Modal {
  connectedCallback() {
    if (this.initialised) return;
    this.initialised = true;

    this.resultsEl = this.querySelector('[data-picker-results]');
    this.bodyEl = this.querySelector('[data-picker-body]');
    this.loadingEl = this.querySelector('[data-picker-loading]');
    this.errorEl = this.querySelector('[data-picker-error]');
    this.counterEl = this.querySelector('[data-picker-counter]');
    this.searchEl = this.querySelector('[data-picker-search]');
    this.confirmEl = this.querySelector('[data-picker-confirm]');
    this.restoreEl = this.querySelector('[data-picker-restore]');
    this.reselectNoteEl = this.querySelector('[data-picker-reselect-note]');
    this.congratsEl = this.querySelector('[data-picker-congrats]');

    this.mode = 'choose';
    this.max = 0;
    this.selected = new Map(); // free-copy variant id -> original product handle
    this.pinnedCards = []; // card elements kept at the head of the first grid
    this.pinnedIds = new Set(); // their variant ids
    this.term = '';
    this.loadedTerm = null;
    this.fetchAborter = null;
    this.busy = false;

    // Openers live in merge-refreshed regions (reward bar, PDP button), so
    // listen once at the document level.
    this.documentClickHandler = (evt) => {
      const opener = evt.target.closest('[data-reward-picker-open]');
      if (!opener) return;
      evt.preventDefault();
      this.openPicker(this.cartHasFreeItems() ? 'swap' : 'choose', { opener });
    };
    document.addEventListener('click', this.documentClickHandler);

    this.reselectHandler = (evt) => {
      const detail = evt.detail || {};
      this.openPicker('reselect', {
        max: detail.allowed,
        showRestore: Boolean(detail.canRestore)
      });
    };
    document.addEventListener('dispatch:reward-picker:reselect', this.reselectHandler);

    // Fired by the reward engine when the customer hits checkout with free
    // tattoos earned but none in the cart.
    this.claimHandler = (evt) => {
      const detail = evt.detail || {};
      this.openPicker('claim', { max: detail.allowed, opener: detail.opener });
    };
    document.addEventListener('dispatch:reward-picker:claim', this.claimHandler);

    // Cards wrap the theme's product-block, which contains PDP links — the
    // click must select, never navigate.
    this.addEventListener('click', (evt) => {
      const card = evt.target.closest('[data-free-tattoo-card]');
      if (!card || !this.contains(card)) return;
      evt.preventDefault();
      this.toggleCard(card);
    });

    this.searchEl.addEventListener('input', () => {
      clearTimeout(this.searchDebounce);
      this.searchDebounce = setTimeout(() => {
        const term = this.searchEl.value.trim();
        if (term === this.term) return;
        this.term = term;
        this.loadResults(1, true);
      }, 300);
    });

    this.confirmEl.addEventListener('click', this.applySelection.bind(this));

    this.restoreEl.addEventListener('click', () => {
      document.dispatchEvent(new CustomEvent('dispatch:reward:restore', { bubbles: true }));
      this.close();
    });

    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const nextPage = parseInt(entry.target.dataset.pickerNextPage, 10);
        this.intersectionObserver.unobserve(entry.target);
        entry.target.remove();
        this.loadResults(nextPage, false);
      });
    }, { root: this.bodyEl, rootMargin: '200px' });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.documentClickHandler);
    document.removeEventListener('dispatch:reward-picker:reselect', this.reselectHandler);
    document.removeEventListener('dispatch:reward-picker:claim', this.claimHandler);
  }

  rewardBar() {
    return document.querySelector('[data-reward-enabled]');
  }

  cartHasFreeItems() {
    const bar = this.rewardBar();
    return bar && parseInt(bar.dataset.rewardFreeqty || '0', 10) > 0;
  }

  allowedFromBar() {
    const bar = this.rewardBar();
    return bar ? parseInt(bar.dataset.rewardAllowed || '0', 10) : 0;
  }

  /**
   * Opens the overlay in the given mode.
   * @param {string} mode - choose | claim | swap | reselect
   * @param {object} opts - { opener, max, showRestore }
   */
  async openPicker(mode, opts = {}) {
    this.mode = mode;
    this.max = typeof opts.max === 'number' ? opts.max : this.allowedFromBar();
    if (this.max <= 0) return;

    this.selected.clear();
    // Swap mode is the only one where an empty selection means something:
    // "remove every free tattoo from the cart".
    this.allowEmptyConfirm = mode === 'swap';
    this.reselectNoteEl.hidden = mode !== 'reselect';
    this.renderCongrats(mode === 'claim');
    this.restoreEl.hidden = !(mode === 'reselect' && opts.showRestore);

    // The opener may sit inside the cart drawer — both surfaces lock body
    // scroll, so close the drawer first (its close restores scroll
    // synchronously) and bring it back after a confirmed selection.
    this.cameFromDrawer = Boolean(document.querySelector('.js-cart-drawer[open]'));
    if (this.cameFromDrawer) {
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
    }

    this.open(opts.opener || null);

    // Pre-select what is already earned/held so the yellow outline shows
    // which tattoos stay in the cart. One cart fetch serves both modes.
    if (mode === 'swap' || mode === 'reselect') {
      let cart = null;
      try {
        cart = await (await fetch(`${this.shopRoot()}cart.js`)).json();
      } catch (e) { /* selection just starts empty */ }

      if (mode === 'swap' && cart) {
        // Cart lines carry the FREE COPY's handle; the pinned-card fetch
        // needs the ORIGINAL's. _reward_picks maps variant id -> original
        // handle, so prefer it and keep the line handle as a last resort.
        const originalHandles = new Map(
          this.previousPicks(cart).map((pick) => [String(pick.id), pick.handle])
        );
        cart.items
          .filter(isFreeLine)
          .slice(0, this.max)
          .forEach((item) => {
            const id = String(item.variant_id);
            this.selected.set(id, originalHandles.get(id) || item.handle);
          });
      } else if (mode === 'reselect') {
        this.previousPicks(cart).slice(0, this.max).forEach((pick) => {
          this.selected.set(String(pick.id), pick.handle);
        });
      }
    }

    // Reuse the cached results only when the pinned set is unchanged:
    // placePinned reorders selected cards to the head of the first grid, so
    // a different selection needs a fresh page 1 to rebuild natural order.
    const sameAsPinned = this.selected.size === this.pinnedIds.size
      && Array.from(this.selected.keys()).every((id) => this.pinnedIds.has(id));

    if (sameAsPinned) {
      this.pinnedTargets = new Set(this.selected.keys());
    } else {
      this.renderPinned();
    }

    if (this.loadedTerm !== this.term || !sameAsPinned) {
      this.loadResults(1, true);
    } else {
      this.syncCardStates();
    }
    this.updateCounter();
  }

  /**
   * Claim-mode headline ("Congrats! You are eligible for up to 3 Free
   * Tattoos"). The copy is server-translated and carries the accent span, so
   * it goes in as HTML; only the count is substituted here — a plural key
   * can't be resolved in Liquid for a number that is only known now.
   * @param {boolean} show - whether the headline applies to this open.
   */
  renderCongrats(show) {
    if (!this.congratsEl) return;
    this.congratsEl.hidden = !show;
    if (!show) return;
    const template = this.max === 1
      ? (this.dataset.congratsOneTemplate || this.dataset.congratsTemplate || '')
      : (this.dataset.congratsTemplate || '');
    this.congratsEl.innerHTML = template.replace('[count]', this.max);
  }

  /**
   * Pins the selected cards to the head of the results grid so they can be
   * unchecked without scrolling — even when they live on page 5 of a
   * 100-product list after a reload. Each card is fetched through the
   * original product's layout-less template
   * (/products/<handle>?view=free-products); a handle that doesn't resolve
   * to an eligible card (stale pick, free-copy handle fallback) is simply
   * left un-pinned in the normal list.
   */
  async renderPinned() {
    this.pinnedCards = [];
    this.pinnedIds = new Set();
    // Everything selected at open time belongs on top — set synchronously so
    // result pages arriving before (or instead of) the card fetches can hand
    // their copy over via placePinned. Live selections made while browsing
    // are deliberately NOT in this set: cards must not jump around mid-tap.
    this.pinnedTargets = new Set(this.selected.keys());

    const entries = [];
    this.selected.forEach((handle, variantId) => {
      if (handle) entries.push([variantId, handle]);
    });
    if (entries.length === 0) return;

    const run = (this.pinnedRun = {});
    const cards = await Promise.all(entries.map(async ([variantId, handle]) => {
      try {
        const url = `${this.shopRoot()}products/${handle}?view=${this.dataset.resultsView}`;
        const response = await fetch(url);
        if (!response.ok) return null;
        const template = document.createElement('template');
        template.innerHTML = await response.text();
        // Matching on the variant id too guards against a pick whose handle
        // now pairs to a different free copy.
        return template.content.querySelector(
          `[data-free-tattoo-card][data-variant-id="${variantId}"]`
        );
      } catch (e) {
        return null;
      }
    }));
    if (this.pinnedRun !== run) return; // superseded by a newer open

    cards.forEach((card) => {
      // A result page may have pinned this id already (placePinned captures
      // selected cards from pages) — skip the fetched copy then.
      if (!card || this.pinnedIds.has(card.dataset.variantId)) return;
      this.pinnedCards.push(card);
      this.pinnedIds.add(card.dataset.variantId);
    });
    this.placePinned();
    this.syncCardStates();
  }

  /**
   * Keeps the pinned cards as the FIRST cards of the first results grid —
   * inline with the others, not a row of their own. Result-page copies of a
   * pinned id are removed; a page card selected at open time but not pinned
   * yet is captured (covers picks whose stored handle couldn't be fetched —
   * e.g. legacy picks holding the free copy's handle). Runs after every
   * pinned render AND every page append — whichever finishes last wins the
   * race. The card elements survive a results reset (search) detached and
   * are re-prepended when the next grid arrives.
   */
  placePinned() {
    if (!this.pinnedTargets || this.pinnedTargets.size === 0) return;

    this.resultsEl.querySelectorAll('[data-free-tattoo-card]').forEach((card) => {
      const id = card.dataset.variantId;
      if (!id || !this.pinnedTargets.has(id)) return;
      if (!this.pinnedIds.has(id)) {
        this.pinnedCards.push(card);
        this.pinnedIds.add(id);
      } else if (!this.pinnedCards.includes(card)) {
        card.remove();
      }
    });

    const firstGrid = this.resultsEl.querySelector('[data-picker-grid]');
    if (firstGrid && this.pinnedCards.length > 0) {
      firstGrid.prepend(...this.pinnedCards);
    }
    this.resultsEl.querySelectorAll('[data-picker-grid]').forEach((grid) => {
      if (!grid.querySelector('[data-free-tattoo-card]')) grid.remove();
    });
  }

  shopRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /**
   * Previously picked free tattoos. The cart attribute is authoritative
   * (it survives devices and is what the app validates against);
   * sessionStorage is the fallback when the cart couldn't be fetched.
   * @param {object} [cart] - Cart object from /cart.js, if already loaded.
   */
  previousPicks(cart) {
    let raw = cart && cart.attributes ? cart.attributes._reward_picks : null;
    if (!raw) {
      try {
        raw = window.sessionStorage.getItem('tattup:rewardPicks');
      } catch (e) { /* private mode */ }
    }
    try {
      const picks = raw ? JSON.parse(raw) : [];
      // Tolerate bare ids as well as {id, handle} objects.
      return picks.map((pick) => (typeof pick === 'object' ? pick : { id: pick, handle: '' }));
    } catch (e) {
      return [];
    }
  }

  /**
   * Results URL. The layout-less `free-products` templates are applied with
   * ?view=, so the response is just the section markup — for the picker
   * collection and for search alike.
   */
  buildResultsUrl(page) {
    const view = this.dataset.resultsView;
    if (this.term) {
      const params = new URLSearchParams({
        view,
        q: this.term,
        type: 'product',
        page
      });
      if (this.dataset.pickerTag) params.set('filter.p.tag', this.dataset.pickerTag);
      return `${this.dataset.searchUrl}?${params.toString()}`;
    }
    const params = new URLSearchParams({ view, page });
    return `${this.dataset.resultsUrl}?${params.toString()}`;
  }

  /**
   * Fetches one results page. `reset` replaces the list (first page / new
   * search term), otherwise the page is appended (infinite scroll).
   */
  async loadResults(page, reset) {
    if (this.fetchAborter) this.fetchAborter.abort();
    this.fetchAborter = new AbortController();
    this.errorEl.hidden = true;
    this.loadingEl.hidden = false;
    if (reset) this.resultsEl.innerHTML = '';

    try {
      const response = await fetch(this.buildResultsUrl(page), { signal: this.fetchAborter.signal });
      if (!response.ok) throw new Error(`Picker results ${response.status}`);
      const html = await response.text();
      const template = document.createElement('template');
      template.innerHTML = html;

      // The response wraps the section in <div id="shopify-section-…">, so
      // query inside it rather than at the root.
      const grid = template.content.querySelector('[data-picker-grid]');
      const empty = template.content.querySelector('[data-picker-empty]');
      const sentinel = template.content.querySelector('[data-picker-next-page]');
      if (grid) this.resultsEl.appendChild(grid);
      if (empty && !this.resultsEl.querySelector('[data-free-tattoo-card]')) {
        this.resultsEl.appendChild(empty);
      }
      if (sentinel) {
        this.resultsEl.appendChild(sentinel);
        this.intersectionObserver.observe(sentinel);
      }

      // A page can be all-skipped (unpaired products) while more pages
      // remain — drop a stale empty state once real cards arrive.
      if (this.resultsEl.querySelector('[data-free-tattoo-card]')) {
        const staleEmpty = this.resultsEl.querySelector('[data-picker-empty]');
        if (staleEmpty) staleEmpty.remove();
      }

      this.loadedTerm = this.term;
      this.placePinned();
      this.syncCardStates();
    } catch (error) {
      if (error.name !== 'AbortError') this.errorEl.hidden = false;
    } finally {
      this.loadingEl.hidden = true;
    }
  }

  toggleCard(card) {
    const variantId = card.dataset.variantId;
    if (!variantId) return;

    if (this.selected.has(variantId)) {
      this.selected.delete(variantId);
    } else if (this.selected.size < this.max) {
      this.selected.set(variantId, card.dataset.productHandle || '');
    } else {
      // At capacity — flash the counter instead of silently ignoring.
      this.counterEl.classList.remove('is-maxed');
      requestAnimationFrame(() => this.counterEl.classList.add('is-maxed'));
      return;
    }
    this.syncCardStates();
    this.updateCounter();
  }

  syncCardStates() {
    this.querySelectorAll('[data-free-tattoo-card]').forEach((card) => {
      const isSelected = this.selected.has(card.dataset.variantId);
      card.classList.toggle('is-selected', isSelected);
      card.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });
  }

  updateCounter() {
    this.counterEl.textContent = (this.dataset.counterTemplate || '[current]/[max]')
      .replace('[current]', this.selected.size)
      .replace('[max]', this.max);
    this.confirmEl.disabled = this.busy
      || (this.selected.size === 0 && !this.allowEmptyConfirm);
  }

  /**
   * Applies the selection to the cart, then closes.
   *
   * ADD runs BEFORE REMOVE on purpose: /cart/add.js is all-or-nothing, so a
   * failed add (sold-out or deleted free product) leaves the cart exactly as
   * it was. The other order would strip the customer's existing free lines
   * and then fail to replace them.
   *
   * The reward engine is suspended for the duration so a cart event fired
   * from the drawer mid-confirm can't interleave with these mutations.
   */
  async applySelection() {
    if (this.busy || (this.selected.size === 0 && !this.allowEmptyConfirm)) return;
    this.busy = true;
    this.confirmEl.disabled = true;
    this.confirmEl.classList.add('btn--loading');
    if (window.TattooRewards) window.TattooRewards.suspend();

    try {
      const root = this.shopRoot();
      const cart = await (await fetch(`${root}cart.js`)).json();
      const freeLines = cart.items.filter(isFreeLine);

      const existingIds = new Set(freeLines.map((item) => String(item.variant_id)));
      const additions = [];
      const picks = [];
      this.selected.forEach((handle, variantId) => {
        picks.push({ id: parseInt(variantId, 10), handle });
        if (!existingIds.has(variantId)) {
          additions.push({
            id: parseInt(variantId, 10),
            quantity: 1,
            properties: { _free_tattoo: '1' }
          });
        }
      });

      if (additions.length > 0) {
        await this.postJson(`${root}cart/add.js`, { items: additions });
      }

      const removals = {};
      freeLines.forEach((item) => {
        if (!this.selected.has(String(item.variant_id))) removals[item.key] = 0;
      });
      if (Object.keys(removals).length > 0) {
        await this.postJson(`${root}cart/update.js`, { updates: removals });
      }

      // `_reward_min_total` + `_reward_currency` ride along with the picks:
      // the qualifying floor these picks require and the currency it is in,
      // so a checkout-stage discount that drops the total below it can be
      // caught by the app before the order is taken.
      const attributes = { _reward_picks: JSON.stringify(picks) };
      if (window.TattooRewards && window.TattooRewards.minTotalAttributes) {
        Object.assign(
          attributes,
          window.TattooRewards.minTotalAttributes(picks.length, cart.currency)
        );
      }
      await this.postJson(`${root}cart/update.js`, { attributes });
      try {
        window.sessionStorage.setItem('tattup:rewardPicks', JSON.stringify(picks));
      } catch (e) { /* private mode */ }

      document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
      this.close();
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
    } catch (error) {
      this.errorEl.hidden = false;
    } finally {
      this.busy = false;
      this.confirmEl.classList.remove('btn--loading');
      this.updateCounter();
      if (window.TattooRewards) window.TattooRewards.resume();
    }
  }

  async postJson(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
    if (!response.ok) throw new Error(`${url} ${response.status}`);
    return response.json();
  }
}

window.customElements.define('free-tattoo-picker', FreeTattooPicker);
